import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, ArrowLeft, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

// Secret code to become admin (change this!)
const ADMIN_SECRET = 'rezeptmeister2024';

export const SetupAdmin = () => {
  const { user, userProfile, updateUserProfile } = useAuth();
  const [secretCode, setSecretCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (secretCode !== ADMIN_SECRET) {
      toast.error('Falscher Geheimcode!');
      return;
    }

    if (!user) {
      toast.error('Du musst angemeldet sein!');
      return;
    }

    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { isAdmin: true });
      await updateUserProfile({ isAdmin: true });
      toast.success('Du bist jetzt Administrator!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Setzen der Admin-Rechte');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-navy mb-4">
            Bitte melde dich zuerst an
          </h2>
          <Link to="/login">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6">
              Zur Anmeldung
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (userProfile?.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-10 h-10 text-sage" />
          </div>
          <h2 className="font-display text-2xl font-bold text-navy mb-2">
            Du bist bereits Administrator!
          </h2>
          <p className="text-olive mb-6">Du hast vollen Zugriff auf alle Funktionen.</p>
          <Link to="/admin">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6">
              Zum Admin-Bereich
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-md mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-navy hover:text-terracotta mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Zurück</span>
        </Link>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-terracotta" />
            </div>
            <h1 className="font-display text-2xl font-bold text-navy">
              Admin-Zugang
            </h1>
            <p className="text-olive mt-2">
              Für Admin-Rechte kontaktiere bitte den Administrator.
            </p>
          </div>

          <div className="bg-bone-alt rounded-2xl p-6 text-center">
            <Mail className="w-10 h-10 text-terracotta mx-auto mb-4" />
            <h3 className="font-display text-lg font-semibold text-navy mb-2">
              Admin kontaktieren
            </h3>
            <p className="text-olive mb-4">
              Schreibe eine E-Mail an den Administrator, um Zugang zu erhalten.
            </p>
            <a 
              href="mailto:info@dryfroggo.de?subject=Admin-Zugang%20Anfrage&body=Hallo,%0A%0Aich%20möchte%20gerne%20Admin-Rechte%20für%20Rezept%20Froggo%20beantragen.%0A%0AMeine%20E-Mail:%20" 
              className="inline-block"
            >
              <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6 gap-2">
                <Mail className="w-4 h-4" />
                E-Mail senden
              </Button>
            </a>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm text-olive text-center mb-4">
              Hast du bereits einen Zugangscode?
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="password"
                value={secretCode}
                onChange={(e) => setSecretCode(e.target.value)}
                placeholder="Geheimcode eingeben..."
                className="py-5 rounded-xl text-center"
                data-testid="admin-secret-input"
              />
              <Button
                type="submit"
                variant="outline"
                className="w-full rounded-xl py-5"
                disabled={loading || !secretCode}
                data-testid="setup-admin-btn"
              >
                {loading ? 'Wird eingerichtet...' : 'Code bestätigen'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
